import { commercialTermsLabel, preparedCommercialDetails, showsStandingAlgaeCharge } from '../_lib/commercial-prepared';
import { SERVICE_AGREEMENT } from '../_lib/agreement-clauses';

// Read-only proposal body shared by the commercial signing page and the
// executed-copy view, so both render the identical multi-section proposal
// (cover, about, reviews, what's included, partnership responsibilities,
// pricing, additional services, terms). Copy mirrors the PDFMonkey
// "Commercial Quote v2" proposal; the visual treatment follows
// cabanapools.com/commercial-pool-service. Region-specific facts (local
// team, county line, phone, reviews) come from q.regional, which the server
// resolves at publish time and the snapshot freezes at signing.

const money = (v) => (v == null || v === '' ? null : `$${v}`);

export function SectionHeading({ eyebrow, children }) {
  return (
    <div className="cp-heading">
      {eyebrow && <div className="cp-eyebrow">{eyebrow}</div>}
      <h2 className="cp-h1">{children}</h2>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg className="cp-check" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="10" />
      <path d="M5.5 10.5l3 3 6-6.5" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Stars() {
  return (
    <span className="cp-stars" aria-label="5 out of 5 stars" role="img">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M10 1.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L10 14.9l-5.3 2.8 1.1-5.9L1.5 7.7l5.9-.8z" />
        </svg>
      ))}
    </span>
  );
}

export function CommercialCover({ config, q }) {
  return (
    <div className="cp-cover">
      <div className="cp-cover-top">
        <img src={config.logo} alt={config.name} className="cp-cover-logo" />
      </div>
      <div className="cp-cover-footer">
        <div className="cp-cover-copy">
          <div className="cp-cover-eyebrow">For HOAs, hotels, multi-family and community facilities</div>
          <h1 className="cp-cover-title">Pool Service Proposal</h1>
          <p className="cp-cover-trust">CPO-certified technicians · $2M liability coverage · Licensed &amp; bonded #1133031</p>
        </div>
        <div className="cp-cover-item">
          <div className="cp-cover-bar" />
          <div>
            <div className="cp-cover-label">The property</div>
            <div className="cp-cover-value">{q.propertyName || q.billingContactName || 'Your property'}</div>
          </div>
        </div>
        <div className="cp-cover-item">
          <div className="cp-cover-bar" />
          <div>
            <div className="cp-cover-label">Prepared for</div>
            <div className="cp-cover-value">{q.prepared?.recipient.name || q.billingContactName || q.propertyName || ''}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Footer sits inside the document column so it centers under the proposal,
// not under the whole viewport, and carries the region's own line.
export function CommercialFooter({ config, regional }) {
  const phone = regional?.phone || config.phone;
  const tel = regional?.tel || `tel:${String(config.phone || '').replace(/[^0-9+]/g, '')}`;
  return (
    <footer className="cp-footer">
      <div className="cp-footer-main">{config.name} · {config.website} · <a href={tel.startsWith('tel:') ? tel : `tel:${tel}`}>{phone}</a></div>
      {regional?.team && <div className="cp-footer-sub">Your {regional.team} commercial team</div>}
    </footer>
  );
}

function Pricing({ q, c }) {
  const p = q.pricing || {};
  const termsLabel = commercialTermsLabel(q);
  const chemicals = p.chemicalsNote || '';
  return (
    <section className="cp-section" id="cp-proposal-section">
      <SectionHeading eyebrow="Your proposal">Pricing</SectionHeading>
      <div className="cp-price-hero">
        {p.seasonal ? (
          <div className="cp-price-seasons">
            <div className="cp-season">
              <div className="cp-season-label">Summer</div>
              <div className="cp-season-price">{money(p.summerRate)}<span>/mo</span></div>
              <div className="cp-season-freq">{p.summerFrequency || ''}</div>
            </div>
            <div className="cp-season">
              <div className="cp-season-label">Winter</div>
              <div className="cp-season-price">{money(p.winterRate)}<span>/mo</span></div>
              <div className="cp-season-freq">{p.winterFrequency || ''}</div>
            </div>
          </div>
        ) : (
          <div className="cp-price-main">
            <div className="cp-price-label">Monthly service</div>
            <div className="cp-price-amount">{money(p.monthlyRate)}<span>/mo</span></div>
            <div className="cp-price-desc">{p.frequency || 'weekly service'}</div>
          </div>
        )}
        <div className="cp-price-facts">
          {chemicals && <div className="cp-price-chip">{chemicals}</div>}
          <div className="cp-price-chip">Invoiced monthly, {termsLabel}</div>
          <div className="cp-price-chip">Month to month, 30 days notice</div>
        </div>
      </div>
      <div className="cp-meta">
        <div className="cp-meta-row"><span className="cp-meta-label">Payment terms</span><span className="cp-meta-value">{termsLabel}, by check or ACH. No card on file.</span></div>
        {q.serviceAddress && <div className="cp-meta-row"><span className="cp-meta-label">Service address</span><span className="cp-meta-value">{q.serviceAddress}</span></div>}
        {p.bodiesOfWater && <div className="cp-meta-row"><span className="cp-meta-label">Bodies of water</span><span className="cp-meta-value">{p.bodiesOfWater}</span></div>}
        {q.startDate && <div className="cp-meta-row"><span className="cp-meta-label">Requested start date</span><span className="cp-meta-value">{q.startDate}</span></div>}
      </div>
      {q.prepared && <dl className="cp-meta" aria-label="Prepared quote details">
        {preparedCommercialDetails(q.prepared).map(([label, value], i) => (
          <div className="cp-meta-row" key={`${label}-${i}`}>
            <dt className="cp-meta-label">{label}</dt>
            <dd className="cp-meta-value" style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</dd>
          </div>
        ))}
      </dl>}
      <div className="cp-addl">
        <div className="cp-addl-head">Billed separately, after completion</div>
        {p.filterCleanPrice != null && (
          <div className="cp-addl-row"><span>Filter cleaning, up to four times per year</span><span>{money(p.filterCleanPrice)} each</span></div>
        )}
        {showsStandingAlgaeCharge(c, q.prepared) && <div className="cp-addl-row"><span>Annual algae prevention treatment</span><span>$100 per year</span></div>}
        <div className="cp-addl-row"><span>Specialty treatments and equipment repairs</span><span>Quoted as needed</span></div>
      </div>
    </section>
  );
}

// "Why properties choose Cabana" items are { title, body } today; signed
// snapshots from before 2026-09-19 carry plain strings.
function WhyCard({ item }) {
  if (item && typeof item === 'object') {
    return (
      <div className="cp-why-card">
        <h3>{item.title}</h3>
        <p>{item.body}</p>
      </div>
    );
  }
  return <div className="cp-why-card"><p>{item}</p></div>;
}

// Real commercial-context reviews for the property's region. A region with
// none renders no reviews block at all rather than borrowed or generic ones.
// Snapshots signed before regional data existed froze a `reviews` block in
// their content; the executed copy must keep showing exactly what was signed,
// so that block is the fallback whenever the snapshot carries no regional data.
function reviewBlock(q, c) {
  const regional = q.regional;
  if (regional && Array.isArray(regional.reviews) && regional.reviews.length > 0) {
    const scoped = regional.reviewsScope === 'regional' && regional.areaName;
    return {
      title: `Trusted across ${scoped ? regional.areaName : 'California'}.`,
      rating: '4.9',
      count: '160+ verified reviews',
      items: regional.reviews,
    };
  }
  const frozen = c && c.reviews;
  if (!regional && frozen && Array.isArray(frozen.items) && frozen.items.length > 0) {
    return {
      title: frozen.title || 'Trusted across California.',
      rating: frozen.rating || '4.9',
      count: frozen.count || '',
      items: frozen.items,
    };
  }
  return null;
}

function Reviews({ block }) {
  if (!block) return null;
  return (
    <section className="cp-section" id="cp-reviews-section">
      <div className="cp-reviews">
        <div className="cp-reviews-head">
          <div>
            <div className="cp-eyebrow">Reviews</div>
            <h2 className="cp-h1">{block.title}</h2>
          </div>
          <div className="cp-reviews-rating">
            <span className="cp-reviews-score">{block.rating}</span>
            <Stars />
            <span className="cp-reviews-count">{block.count}</span>
          </div>
        </div>
        <div className="cp-review-grid">
          {block.items.map((review) => (
            <figure key={`${review.name}-${review.quote.slice(0, 24)}`} className="cp-review">
              <Stars />
              <blockquote>{review.quote}</blockquote>
              <figcaption>
                <strong>{review.name}</strong>
                <span>{[review.role, review.platform].filter(Boolean).join(' · ')}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function CommercialProposalSections({ config, q, content }) {
  const c = content || SERVICE_AGREEMENT.commercial;
  const p = q.pricing || {};
  const why = Array.isArray(c.about?.why) ? c.about.why : [];
  // Signed snapshots from before 2026-09-19 carry two blocks the current
  // template no longer has. The executed copy must still show everything the
  // signer saw, so render them whenever the frozen content includes them.
  const credentials = c.credentials || { items: [] };
  const howWeWork = c.howWeWork || { steps: [] };
  return (
    <>
      <section className="cp-section" id="cp-about-section">
        <SectionHeading eyebrow="Commercial pool specialists">About {config.name}</SectionHeading>
        {c.about.intro.map((para, i) => <p key={i} className="cp-p">{para}</p>)}
        {q.regional?.localLine && <p className="cp-p">{q.regional.localLine}</p>}
        {q.authority && (
          <aside className="cp-authority" aria-label="Commercial service context">
            <div>
              <h3>{q.authority.title}</h3>
              <p>{q.authority.body}</p>
            </div>
          </aside>
        )}
        {Array.isArray(credentials.items) && credentials.items.length > 0 && (
          <div className="cp-proof" id="cp-credentials-section">
            <h3 className="cp-h2">{credentials.title}</h3>
            <dl>
              {credentials.items.map((item) => (
                <div key={item.title}>
                  <dt>{item.title}</dt>
                  <dd>{item.body}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        <h3 className="cp-h2">{c.about.whyTitle}</h3>
        <div className="cp-why-grid">
          {why.map((item, i) => <WhyCard key={i} item={item} />)}
        </div>
        <p className="cp-p">{c.about.closing}</p>
      </section>

      {Array.isArray(howWeWork.steps) && howWeWork.steps.length > 0 && (
        <section className="cp-section" id="cp-how-section">
          <SectionHeading>{howWeWork.title || 'How service works'}</SectionHeading>
          {howWeWork.intro && <p className="cp-p">{howWeWork.intro}</p>}
          <ol className="cp-steps">
            {howWeWork.steps.map((step, i) => (
              <li key={step.title}>
                <span className="cp-step-num" aria-hidden="true">{i + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      <Reviews block={reviewBlock(q, c)} />

      <section className="cp-section" id="cp-included-section">
        <SectionHeading eyebrow="Every visit">What's included</SectionHeading>
        <ul className="cp-included">
          {c.whatsIncluded.map((li, i) => <li key={i}><CheckIcon /><span>{li}</span></li>)}
          {p.bodiesOfWater && <li><CheckIcon /><span>Bodies of water: {p.bodiesOfWater}</span></li>}
        </ul>
      </section>

      <section className="cp-section" id="cp-responsibilities-section">
        <SectionHeading eyebrow="How we work together">Partnership responsibilities</SectionHeading>
        <div className="cp-resp-grid">
          <div className="cp-resp">
            <h3>Your responsibilities</h3>
            <ul className="cp-list">{c.responsibilities.yours.map((li, i) => <li key={i}>{li}</li>)}</ul>
          </div>
          <div className="cp-resp cp-resp-ours">
            <h3>Cabana's commitments</h3>
            <ul className="cp-list">{c.responsibilities.ours.map((li, i) => <li key={i}>{li}</li>)}</ul>
          </div>
        </div>
      </section>

      <Pricing q={q} c={c} />

      <section className="cp-section" id="cp-additional-section">
        <SectionHeading eyebrow="Billed after completion">Additional services</SectionHeading>
        {c.additionalServices.map((s, i) => (
          <div key={i}>
            <h3 className="cp-h2" style={i === 0 ? { marginTop: 0 } : undefined}>{s.title}</h3>
            <p className="cp-p">{s.body}</p>
          </div>
        ))}
      </section>

      <section className="cp-section" id="cp-terms-section">
        <SectionHeading eyebrow="Terms">Terms and conditions</SectionHeading>
        {c.terms.map((t, i) => (
          <div key={i}>
            <h3 className="cp-h2" style={i === 0 ? { marginTop: 0 } : undefined}>{t.title}</h3>
            {t.intro && <p className="cp-p">{t.intro}</p>}
            {Array.isArray(t.bullets) && t.bullets.length > 0 && (
              <ul className="cp-list">{t.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>
            )}
            {t.body && <p className="cp-p">{t.body}</p>}
          </div>
        ))}
      </section>
    </>
  );
}
